import React, { useEffect, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';

const ProgressSlide: React.FC = () => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [centerIndex, setCenterIndex] = React.useState<number>(-1);

  const parseBoldText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return (
          <Box
            component="span"
            key={index}
            sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}
          >
            {boldText}
          </Box>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const eventWidth = 400;  // Increased spacing for all events

  const events = [
    {
      "date": "Jan 2024",
      "title": "User Onboarding",
      "description": "• First users onboarded\n• Positive reviews received",
      "icon": GroupsIcon
    },
    {
      "date": "Feb 2024",
      "title": "Inman News Review",
      "description": "• **Four-star review** from Inman News",
      "icon": EmojiEventsIcon,
      "logo": "https://logo.clearbit.com/inman.com"
    },
    {
      "date": "Spring 2024",
      "title": "Product Improvement",
      "description": "• GPT-4o integration\n• Enhanced reliability\n• First large brokerage onboarded",
    },
    {
      "date": "Jun 2024",
      "title": "Feature Launch",
      "description": "• **Multi-agent workflow** launched\n• AI website readers\n• Human-in-the-loop handoffs",
    },
    {
      "date": "Jul 2024",
      "title": "Inman Connect",
      "description": "• Pitched on stage\n• New brokerage customers\n• MLS partnership talks initiated",
      "logo": "https://logo.clearbit.com/inman.com"
    },
    {
      "date": "Aug 2024",
      "title": "Call Transcription Beta",
      "description": "• Call transcription rolled out\n• Select users testing",
      "icon": PhoneIcon
    },
    {
      "date": "Sep 2024",
      "title": "Blueprint Conference",
      "description": "• Influencer networking\n• MLS meetings lined up\n• Tech partner discussions",
      "logo": "https://logo.clearbit.com/blueprintvegas.com"
    },
    {
      "date": "Sep 2024",
      "title": "T360 Pitch Battle",
      "description": "• Pitched in New Orleans\n• **25+ executives** joined waitlist\n• MLS traction accelerated",
      "logo": "https://logo.clearbit.com/t360.com"
    },
    {
      "date": "Oct 2024",
      "title": "Press Recognition",
      "description": "• Additional Inman coverage\n• Two large MLSs in talks",
      "logo": "https://logo.clearbit.com/inman.com"
    },
    {
      "date": "Oct 2024",
      "title": "Best AI Startup",
      "description": "• **Inman: \"Best AI Startup in Real Estate\"**",
      "icon": EmojiEventsIcon,
      "logo": "https://logo.clearbit.com/inman.com"
    },
    {
      "date": "Oct 2024",
      "title": "T360 Partnership",
      "description": "• Engaged **T360** consulting\n• GTM strategy\n• Waitlist optimization",
      "icon": IntegrationInstructionsIcon,
      "logo": "https://logo.clearbit.com/t360.com"
    },
    {
      "date": "Nov 2024",
      "title": "NAR NXT Conference",
      "description": "• Live demos\n• Live calling demonstrations",
      "logo": "https://logo.clearbit.com/nar.realtor"
    },
    {
      "date": "Dec 2024",
      "title": "MLS Forum Pitch",
      "description": "• Pitched via live webinar\n• MLS forum audience",
      "logo": "https://logo.clearbit.com/mlsforum.org"
    },
    {
      "date": "Dec 2024",
      "title": "Automated Follow-up",
      "description": "• **Intelligent automated follow-up** launched\n• AI nurtures leads automatically",
      "icon": RocketLaunchIcon
    },
    {
      "date": "Feb 2025",
      "title": "Smart Contact Summaries",
      "description": "• **Relationship intelligence hub**\n• Comprehensive contact insights",
    },
    {
      "date": "Mar 2025",
      "title": "Oppy V2 Public Launch",
      "description": "• **March 4th official launch**\n• Voice, SMS, email, web chat\n• California Association of Realtors (CAR) conference speaker",
      "icon": RocketLaunchIcon,
      "logo": "https://logo.clearbit.com/car.org",
      "logoName": "California Association of Realtors"
    },
    {
      "date": "Apr 2025",
      "title": "Document Analyzer",
      "description": "• **Free AI contract review tool**\n• Missing signatures & errors detected\n• Compliance checking",
    },
    {
      "date": "May 2025",
      "title": "Early Adopter Growth",
      "description": "• **eHomes: 5k → 100k+ leads**\n• **TheQwikFix** onboarded\n• Arrival Home Loans onboarded",
      "icon": TrendingUpIcon,
      "logos": [
        "/logos/ehomes-logo.png",
        "https://logo.clearbit.com/theqwikfix.com"
      ],
      "logoNames": ["eHomes", "TheQwikFix"]
    },
    {
      "date": "Jun 2025",
      "title": "Voice Calling Dev",
      "description": "• **AI voice calling** finalized\n• Outbound & inbound ready\n• Production prep completed",
      "icon": PhoneIcon
    },
    {
      "date": "Jul 2025",
      "title": "Follow Up Boss Integration",
      "description": "• **Follow Up Boss CRM** partnership\n• **3-second AI response** (9,000× faster)\n• **2.5× conversion increase**",
      "icon": IntegrationInstructionsIcon,
      "logo": "https://logo.clearbit.com/followupboss.com",
      "logoName": "Follow Up Boss"
    },
    {
      "date": "Aug 2025",
      "title": "AI Voice Launch",
      "description": "• **Production AI Voice** launched\n• Presented twice at Inman Connect\n• **Named 2025 Future Leader**",
      "icon": EmojiEventsIcon,
      "logo": "https://logo.clearbit.com/inman.com",
      "logoName": "Inman Connect"
    },
    {
      "date": "Sep 2025",
      "title": "LiveKit & Scaling",
      "description": "• **LiveKit telephony** transition complete\n• **Orchard** customer onboarded\n• **Homes of Idaho** customer\n• Presented at Blueprint conference\n• ClearCast podcast press",
      "icon": IntegrationInstructionsIcon,
      "logos": [
        "https://logo.clearbit.com/livekit.io",
        "https://logo.clearbit.com/orchard.com",
        "https://logo.clearbit.com/homesofidaho.com",
        "https://logo.clearbit.com/blueprintvegas.com",
        "https://logo.clearbit.com/clearcapital.com"
      ],
      "logoNames": ["LiveKit", "Orchard", "Homes of Idaho", "Blueprint Vegas", "ClearCast"]
    },
    {
      "date": "Sep 2025",
      "title": "NAHREP Speaker",
      "description": "• Presented at **NAHREP** conference\n• National Association of Hispanic Realtors\n• Showcased AI for real estate pros",
      "icon": EmojiEventsIcon,
      "logo": "https://logo.clearbit.com/nahrep.org"
    },
    {
      "date": "Now",
      "title": "Building Sales Team",
      "description": "• **Strong customer funnel**\n• Full-time sales director hired\n• Accelerating customer closing",
      "isNow": true,
      "icon": BusinessIcon
    },
    {
      "date": "Q4 2025",
      "title": "Enterprise Expansion",
      "description": "• Enterprise brokerage scaling\n• Strategic partnerships\n• Industry-wide adoption",
      "isFuture": true,
      "icon": RocketLaunchIcon
    }
  ];

  const reversedEvents = [...events].reverse();
  const totalWidth = reversedEvents.length * eventWidth + 400;

  // Handle scroll to detect center event
  const handleScroll = () => {
    if (scrollRef.current) {
      const containerWidth = scrollRef.current.clientWidth;
      const scrollLeft = scrollRef.current.scrollLeft;
      const centerPosition = scrollLeft + containerWidth / 2;
      
      // Calculate which event is closest to center
      // Account for 200px left padding
      const adjustedCenterPosition = centerPosition - 200;
      const newCenterIndex = Math.floor(adjustedCenterPosition / eventWidth);
      
      // Clamp to valid range
      const clampedIndex = Math.max(0, Math.min(reversedEvents.length - 1, newCenterIndex));
      
      if (clampedIndex !== centerIndex) {
        setCenterIndex(clampedIndex);
      }
    }
  };

  // Auto-scroll when component becomes visible using IntersectionObserver
  useEffect(() => {
    let animationId: number | null = null;
    let isScrolling = false;
    let hasScrolled = false; // Track if we've already auto-scrolled
    
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasScrolled && scrollRef.current) {
          // Component is visible, start auto-scroll
          hasScrolled = true; // Only auto-scroll once
          isScrolling = true;
          
          // Reset to left
          scrollRef.current.scrollLeft = 0;
          
          const scrollSpeed = 1.38; // Pixels per frame (15% faster)
          
          const autoScroll = () => {
            if (scrollRef.current && isScrolling) {
              const maxScroll = scrollRef.current.scrollWidth - scrollRef.current.clientWidth;
              
              if (scrollRef.current.scrollLeft < maxScroll - 1) {
                scrollRef.current.scrollLeft += scrollSpeed;
                animationId = requestAnimationFrame(autoScroll);
              } else {
                // Reached the end
                isScrolling = false;
              }
            }
          };
          
          // Start scrolling after a brief pause to let the slide settle
          setTimeout(() => {
            if (isScrolling && scrollRef.current) {
              animationId = requestAnimationFrame(autoScroll);
            }
          }, 800);
        }
      });
    };
    
    // Create observer
    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.5, // Trigger when 50% visible
    });
    
    // Observe the timeline container
    if (scrollRef.current) {
      observer.observe(scrollRef.current);
      
      // Stop on user interaction
      const handleUserScroll = () => {
        isScrolling = false;
        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
      };
      
      scrollRef.current.addEventListener('wheel', handleUserScroll);
      scrollRef.current.addEventListener('mousedown', handleUserScroll);
      scrollRef.current.addEventListener('touchstart', handleUserScroll);
      
      // Cleanup
      return () => {
        if (scrollRef.current) {
          observer.unobserve(scrollRef.current);
          scrollRef.current.removeEventListener('wheel', handleUserScroll);
          scrollRef.current.removeEventListener('mousedown', handleUserScroll);
          scrollRef.current.removeEventListener('touchstart', handleUserScroll);
        }
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 150px)',
        color: theme.palette.text.primary,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          padding: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h3" sx={{ mb: 1, fontWeight: 'bold' }}>
          Timeline & Road Ahead
        </Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          (Scroll horizontally to view)
        </Typography>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          position: 'relative',
          minHeight: 0,
          paddingLeft: '200px',
          paddingRight: '200px',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: `${totalWidth}px`,
            height: '100%',
            minHeight: '600px',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '300px',
              left: '0',
              width: '100%',
              height: '2px',
              backgroundColor: theme.palette.secondary.main,
            }}
          />

          {reversedEvents.map((event, index) => {
            // All events on bottom for consistency
            const isTopEvent = false;
            const isCenter = index === centerIndex;

            const leftPosition = `${index * eventWidth + 200}px`;
            const topPosition = '297px';

            const IconComponent = (event as any).icon;

            return (
              <Box
                key={index}
                sx={{
                  position: 'absolute',
                  left: leftPosition,
                  top: topPosition,
                  transform: isCenter 
                    ? 'translate(-50%, 0) scale(1.1)' 
                    : 'translate(-50%, 0) scale(1)',
                  display: 'flex',
                  flexDirection: isTopEvent ? 'column-reverse' : 'column',
                  alignItems: 'center',
                  justifyContent: isTopEvent ? 'flex-start' : 'flex-end',
                  alignContent: 'space-between',
                  transition: 'transform 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    width: event.isNow ? '24px' : '12px',
                    height: event.isNow ? '24px' : '12px',
                    borderRadius: '50%',
                    backgroundColor: event.isNow
                      ? theme.palette.secondary.main
                      : theme.palette.text.primary,
                    border: event.isNow ? `2px solid ${theme.palette.text.primary}` : 'none',
                  }}
                />

                <Box
                  sx={{
                    textAlign: 'center',
                    maxWidth: '340px',
                    mt: isTopEvent ? 0 : '8px',
                    mb: isTopEvent ? '8px' : 0,
                    minHeight: '80px',
                    ...(isCenter && {
                      backgroundColor: theme.palette.background.glass,
                      borderRadius: '8px',
                      padding: '12px',
                      border: `1px solid ${theme.palette.background.overlayMedium}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 12px rgba(73, 73, 200, 0.15)'
                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    }),
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: event.isNow ? 'bold' : 'normal',
                      opacity: event.isFuture ? 0.5 : 1,
                      fontSize: '1.8rem',
                    }}
                  >
                    {event.date}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    {IconComponent && (
                      <IconComponent sx={{ fontSize: 24, color: theme.palette.secondary.main }} />
                    )}
                    {(event as any).logo && (
                      <Tooltip title={(event as any).logoName || event.title} placement="top" arrow>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: '4px',
                            padding: '2px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.5)',
                              zIndex: 10,
                            }
                          }}
                        >
                          <img 
                            src={(event as any).logo} 
                            alt={(event as any).logoName || event.title}
                            style={{ 
                              height: '20px', 
                              width: 'auto',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Box>
                      </Tooltip>
                    )}
                    {(event as any).logos && (event as any).logos.map((logoUrl: string, logoIndex: number) => {
                      const logoNames = (event as any).logoNames || [];
                      const logoName = logoNames[logoIndex] || event.title;
                      return (
                        <Tooltip key={logoIndex} title={logoName} placement="top" arrow>
                          <Box
                            sx={{
                              backgroundColor: theme.palette.background.paper,
                              borderRadius: '4px',
                              padding: '2px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              mx: 0.5,
                              transition: 'transform 0.2s ease',
                              '&:hover': {
                                transform: 'scale(1.5)',
                                zIndex: 10,
                              }
                            }}
                          >
                            <img 
                              src={logoUrl} 
                              alt={logoName}
                              style={{ 
                                height: '18px', 
                                width: 'auto',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })}
                    <Typography
                      variant="h6"
                      sx={{ 
                        opacity: event.isFuture ? 0.5 : 1,
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                      }}
                    >
                      {event.title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      opacity: event.isFuture ? 0.5 : 1,
                      whiteSpace: 'pre-line',
                      textAlign: 'left',
                      lineHeight: 1.6,
                      fontSize: '1.1rem',
                    }}
                  >
                    {parseBoldText(event.description)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressSlide;
